import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['hidden', 'checkbox', 'newTag', 'suggestionsContainer']

  connect() {
    this.syncHiddenField()
  }

  toggle() {
    this.syncHiddenField()
  }

  addNew(event) {
    event.preventDefault()

    const newTag = this.newTagTarget.value.trim().toLowerCase()
    if (!newTag) return

    // Check if tag already exists as a checkbox
    const existingCheckbox = this.checkboxTargets.find(
      (cb) => cb.value.toLowerCase() === newTag
    )
    if (existingCheckbox) {
      existingCheckbox.checked = true
      this.newTagTarget.value = ''
      this.syncHiddenField()
      return
    }

    // Create new checkbox for the tag
    const container = this.element.querySelector('.d-flex.flex-wrap')
    const id = `tag_${newTag.replace(/\s+/g, '-')}`

    const input = document.createElement('input')
    input.type = 'checkbox'
    input.className = 'btn-check'
    input.id = id
    input.value = newTag
    input.checked = true
    input.autocomplete = 'off'
    input.setAttribute('data-action', 'change->tag-selector#toggle')
    input.setAttribute('data-tag-selector-target', 'checkbox')

    const label = document.createElement('label')
    label.className = 'btn btn-sm btn-outline-secondary rounded-pill'
    label.setAttribute('for', id)
    label.textContent = newTag

    container.appendChild(input)
    container.appendChild(label)
    this.newTagTarget.value = ''
    this.syncHiddenField()
  }

  syncHiddenField() {
    const selectedTags = this.checkboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.value)

    this.hiddenTarget.value = selectedTags.join(',')
  }

  // Called by image analyzer to set tags from AI
  setTags(tags) {
    // First, uncheck all
    this.checkboxTargets.forEach((cb) => (cb.checked = false))

    tags.forEach((tag) => {
      const normalizedTag = tag.toLowerCase().trim()

      // Find matching checkbox
      const checkbox = this.checkboxTargets.find(
        (cb) => cb.value.toLowerCase() === normalizedTag
      )

      if (checkbox) {
        checkbox.checked = true
      } else {
        // Add new tag if it doesn't exist
        this.newTagTarget.value = normalizedTag
        this.addNew(new Event('click'))
      }
    })

    this.syncHiddenField()
  }

  // Allow merging new tags without unchecking existing ones
  addTags(tags) {
    tags.forEach((tag) => {
      const normalizedTag = tag.toLowerCase().trim()

      const checkbox = this.checkboxTargets.find(
        (cb) => cb.value.toLowerCase() === normalizedTag
      )

      if (checkbox) {
        checkbox.checked = true
      } else {
        this.newTagTarget.value = normalizedTag
        this.addNew(new Event('click'))
      }
    })

    this.syncHiddenField()
  }

  // Handle AI suggestions: check existing tags, show suggestions for new ones
  addAISuggestions(existingTags, suggestedTags) {
    // Check existing tags in the main list
    existingTags.forEach((tag) => {
      const normalizedTag = tag.toLowerCase().trim()
      const checkbox = this.checkboxTargets.find(
        (cb) => cb.value.toLowerCase() === normalizedTag
      )
      if (checkbox) {
        checkbox.checked = true
      }
    })

    // Show suggestions section for new tags
    if (suggestedTags.length > 0) {
      this.showSuggestions(suggestedTags)
    }

    this.syncHiddenField()
  }

  showSuggestions(tags) {
    this.clearSuggestions()

    const container = document.createElement('div')
    container.className =
      'mt-2 mb-2 p-2 bg-info-subtle border border-info-subtle rounded'
    container.setAttribute('data-tag-selector-target', 'suggestionsContainer')

    const header = document.createElement('div')
    header.className = 'd-flex justify-content-between align-items-center mb-2'
    header.innerHTML = `
      <small class="text-info-emphasis fw-medium">
        Suggested new tags
      </small>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm btn-link text-success p-0"
                data-action="click->tag-selector#acceptAllSuggestions">
          Accept all
        </button>
        <button type="button" class="btn btn-sm btn-link text-muted p-0"
                data-action="click->tag-selector#clearSuggestions">
          Dismiss all
        </button>
      </div>
    `

    const tagsContainer = document.createElement('div')
    tagsContainer.className = 'd-flex flex-wrap gap-1'

    tags.forEach((tag) => {
      const badge = this.createSuggestionBadge(tag)
      tagsContainer.appendChild(badge)
    })

    container.appendChild(header)
    container.appendChild(tagsContainer)

    // Insert before the "Add new tag" input group
    const inputGroup = this.element.querySelector('.input-group')
    inputGroup.parentNode.insertBefore(container, inputGroup)
  }

  createSuggestionBadge(tag) {
    const wrapper = document.createElement('span')
    wrapper.className =
      'badge bg-info text-info-emphasis d-inline-flex flex-column align-items-center py-1 px-2 rounded-pill'
    wrapper.innerHTML = `
      <span class="fw-medium fs-6">${tag}</span>
      <div class="d-flex gap-2 small">
        <button type="button" class="btn btn-sm p-0 text-success border-0 bg-transparent fw-medium"
                data-action="click->tag-selector#acceptSuggestion"
                data-tag="${tag}">
          Accept
        </button>
        <button type="button" class="btn btn-sm p-0 text-muted border-0 bg-transparent"
                data-action="click->tag-selector#rejectSuggestion"
                data-tag="${tag}">
          Dismiss
        </button>
      </div>
    `
    return wrapper
  }

  acceptAllSuggestions(event) {
    event.preventDefault()
    if (!this.hasSuggestionsContainerTarget) return

    const badges = this.suggestionsContainerTarget.querySelectorAll('.badge')
    badges.forEach((badge) => {
      const tag = badge.querySelector('[data-tag]').dataset.tag
      this.newTagTarget.value = tag
      this.addNew(new Event('click'))
    })

    this.clearSuggestions()
  }

  acceptSuggestion(event) {
    event.preventDefault()
    const tag = event.currentTarget.dataset.tag

    // Add tag to main list using existing logic
    this.newTagTarget.value = tag
    this.addNew(new Event('click'))

    // Remove from suggestions
    this.removeSuggestionBadge(event.currentTarget)
  }

  rejectSuggestion(event) {
    event.preventDefault()
    this.removeSuggestionBadge(event.currentTarget)
  }

  removeSuggestionBadge(button) {
    const badge = button.closest('.badge')
    badge.remove()

    // If no more suggestions, remove the container
    if (this.hasSuggestionsContainerTarget) {
      const remaining =
        this.suggestionsContainerTarget.querySelectorAll('.badge')
      if (remaining.length === 0) {
        this.clearSuggestions()
      }
    }
  }

  clearSuggestions() {
    if (this.hasSuggestionsContainerTarget) {
      this.suggestionsContainerTarget.remove()
    }
  }
}
